import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';

import TextField from '../components/TextField';
import FullScreenDialog from '../components/FullScreenDialog';
import DatePicker from '../components/DatePicker';

import useDiaryData from '../hooks/useDiaryData';
import useQueryParams from '../hooks/useQueryParams';
import useSnackbarText from '../hooks/useSnackbarText';

import todoes from '../api/todoes';
import { maxWidth } from '../components/configs';

const queryParamKey = 'create-todo';

const generateDefaultState = selectedDate => {
  return {
    date: selectedDate,
    name: '',
    remarks: '',
  };
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  submitButton: {
    maxWidth,
    width: '100%',
    marginTop: 20,
    marginBottom: 20,
    color: '#fff',
    borderRadius: 6,
    fontWeight: 600,
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
}));

function capitalizeFirstLetterOfEachWord(str) {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function CreateTodoDialog() {
  const classes = useStyles();
  // eslint-disable-next-line
  const [diary, _, handleSetDate, handleManualFetch] = useDiaryData();
  const [queryParams, setQueryParams] = useQueryParams();
  const [disabled, setDisabled] = useState(false);
  const [state, _setState] = useState(generateDefaultState(diary.selectedDate));
  const [error, setError] = useState({});
  const handleSnackbarChange = useSnackbarText();

  const queryValue = queryParams[queryParamKey];

  const setState = useCallback(newState => {
    _setState(prevState => ({
      ...prevState,
      ...newState,
    }));
  }, []);

  const handleChange = type => (event, _value) => {
    let value = _value === undefined ? event.target.value : _value;

    if (type === 'name') {
      value = capitalizeFirstLetterOfEachWord(value);
    }

    setState({ [type]: value });
  };

  const handleDateChange = type => (event, value) => {
    setState({ [type]: value });
  };

  const handleValueCheck = () => {
    let noError = true;
    let error = {};
    if (!state.name) {
      noError = false;
      error.name = 'Invalid value.';
    }

    if (!state.date) {
      noError = false;
      error.date = 'Invalid value.';
    }
    setError(error);
    return noError;
  };

  const handleSubmit = async () => {
    if (!handleValueCheck()) {
      handleSnackbarChange({ text: 'Invalid inputs', variant: 'error' });
      return;
    }

    setDisabled(true);
    const { data, success } = await todoes.createTodo({
      name: state.name,
      date: new Date(state.date),
      remarks: state.remarks,
    });
    setDisabled(false);

    if (!success) {
      handleSnackbarChange({ text: data.message, variant: 'error' });
      return;
    } else {
      handleSnackbarChange({ text: 'To do list created', variant: 'success' });
      handleManualFetch();
      setQueryParams({ [queryParamKey]: null });
    }
  };

  useEffect(() => {
    _setState(generateDefaultState(diary.selectedDate));
  }, [queryValue, diary.selectedDate]);

  return (
    <FullScreenDialog
      title="Create New To Do List"
      queryParamKey={queryParamKey}
    >
      <div className={classes.container}>
        <DatePicker
          label="Date"
          value={state.date}
          onChange={handleDateChange('date')}
          error={Boolean(error.date)}
          helperText={error.date}
          autoApply
        />

        <TextField
          label="Title*"
          value={state.name}
          onChange={handleChange('name')}
          error={Boolean(error.name)}
          helperText={error.name}
        />

        <TextField
          label="Remarks"
          value={state.remarks}
          onChange={handleChange('remarks')}
          error={Boolean(error.remarks)}
          helperText={error.remarks}
        />

        <Button
          className={classes.submitButton}
          onClick={handleSubmit}
          disabled={disabled}
        >
          Create
        </Button>
      </div>
    </FullScreenDialog>
  );
}

export default CreateTodoDialog;
